'use client';

import { useGetUserQuery, useLogoutMutation } from '@repo/store/maps/api/authApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectCurrentUser, selectIsAuthenticated } from '@repo/store/maps/slices/auth';
// import { refreshFeatureFlags } from '@repo/store/maps/slices/featureFlags';
import type { WebAuthErrorCode } from '@repo/types/analytic-events/website-analytic-events';
import { type LoginCredentials, loginAction, type RegisterCredentials, type RegisterResult, registerAction } from '@repo/wemod-api-client/actions/auth-actions';
import { refreshTokenWithTimeout } from '@repo/wemod-api-client/auth/refresh-token-with-timeout';
import { useState } from 'react';
export class LoginError extends Error {
  public readonly errorCode: WebAuthErrorCode;
  constructor(message: string, errorCode: WebAuthErrorCode) {
    super(message);
    this.name = 'LoginError';
    this.errorCode = errorCode;
  }
}
export class RegistrationError extends Error {
  public readonly errorCode: WebAuthErrorCode;
  public readonly fieldErrors?: Record<string, string[]>;
  constructor(message: string, errorCode: WebAuthErrorCode, fieldErrors?: Record<string, string[]>) {
    super(message);
    this.name = 'RegistrationError';
    this.errorCode = errorCode;
    this.fieldErrors = fieldErrors;
  }
}
export const useAuth = () => {
  // const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [authActionLoading, setAuthActionLoading] = useState(false);
  const [authActionError, setAuthActionError] = useState<string | null>(null);
  const [logoutUser] = useLogoutMutation();
  const {
    refetch: refreshUser,
    isLoading: isUserQueryLoading
  } = useGetUserQuery(undefined, {
    skip: Boolean(user) || Boolean(authActionError) && !isAuthenticated
  });
  const refresh = async () => {
    await refreshUser().unwrap();
  };
  const login = async (credentials: LoginCredentials) => {
    setAuthActionLoading(true);
    setAuthActionError(null);
    try {
      const result = await loginAction(credentials);
      if (!result.success) {
        setAuthActionError(result.error);
        throw new LoginError(result.error, result.errorCode);
      }
      try {
        await refreshTokenWithTimeout();
        await refreshUser().unwrap();
        // Temporarily disabled due to API throttling for anonymous wand.com visitors:
        // https://linear.app/wemod/issue/WAND-7058/try-to-forward-cf-ip-headers-during-wandcom-ssr-api-requests
        // await dispatch(refreshFeatureFlags());
      } catch (refreshError) {
        console.warn('[useAuth.login] post-login refresh failed', refreshError);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setAuthActionError(errorMessage);
      throw error;
    } finally {
      setAuthActionLoading(false);
    }
  };
  const register = async (credentials_0: RegisterCredentials): Promise<RegisterResult> => {
    setAuthActionLoading(true);
    setAuthActionError(null);
    try {
      const result_0 = await registerAction(credentials_0);
      if (!result_0.success) {
        setAuthActionError(result_0.error);
        throw new RegistrationError(result_0.error, result_0.errorCode, result_0.fieldErrors);
      }
      try {
        await refreshTokenWithTimeout();
        await refreshUser().unwrap();
        // Temporarily disabled due to API throttling for anonymous wand.com visitors:
        // https://linear.app/wemod/issue/WAND-7058/try-to-forward-cf-ip-headers-during-wandcom-ssr-api-requests
        // await dispatch(refreshFeatureFlags());
      } catch (refreshError_0) {
        console.warn('[useAuth.register] post-register refresh failed', refreshError_0);
      }
      return result_0;
    } catch (error_0) {
      if (!(error_0 instanceof RegistrationError)) {
        const errorMessage_0 = error_0 instanceof Error ? error_0.message : 'Registration failed';
        setAuthActionError(errorMessage_0);
      }
      throw error_0;
    } finally {
      setAuthActionLoading(false);
    }
  };
  const logout = async () => {
    await logoutUser().unwrap();
  };
  return {
    user,
    isAuthenticated,
    loading: authActionLoading || isUserQueryLoading,
    error: authActionError ? {
      status: 'CUSTOM_ERROR',
      error: authActionError
    } : undefined,
    refresh,
    login,
    register,
    logout
  };
};